import { Col, createModal, Divider, ModalProps, Row, useModal, useToast } from "@commonlibs";
import useTranslate from "ui/locale/hooks/useTranslate";
import clsx from "clsx";
import Modal from "ui/common/components/feedback/Modal/Modal";
import { useState } from "react";
import { ToolbarButton } from "./ModifyReleaseStrategy.styles";
import { ModifyReleaseStrategyDTO, modifyReleaseStrategySchema } from "../schemas/ModifyReleaseStrategySchema";
import ModifyReleaseStrategyForm from "./ModifyReleaseStrategyForm/ModifyReleaseStrategyForm";
import { ModifyReleaseStrategyProps } from "./ModifyReleaseStrategy.types";
import useUpdateApprover from "features/release-strategy/hooks/useUpdateApprover";
import useCreateApprover from "features/release-strategy/hooks/useCreateApprover";
import { useQueryClient } from "react-query";
import { Queries } from "ui/query/queries";
import type { ApproverDto } from "common/models/api/models/ApproverDto";
import type { CreateApproverDto } from "common/models/api/models/CreateApproverDto";
import { RoleType } from "common/models/role/Role";

const ModifyReleaseStrategyModal = createModal(
    ({ releaseStrategy, className, ...rest }: ModalProps & Omit<ModifyReleaseStrategyProps, "onChange">) => {
        const translate = useTranslate();
        const { hideModal } = useModal();
        const { showToast } = useToast();
        const queryClient = useQueryClient();

        const { isLoading: isUpdatingApprover } = useUpdateApprover({ onSuccess: handleOnSuccessSend });
        const { mutate: createApproverMutation, isLoading: isCreatingApprover } = useCreateApprover({ onSuccess: handleOnSuccessSend });

        const [isValidForm, setIsValidForm] = useState<boolean>(false);
        const [form, setForm] = useState<ModifyReleaseStrategyDTO>({
            name: releaseStrategy.approver?.name || "",
            phone: releaseStrategy.approver?.phone || "",
            email: releaseStrategy.approver?.email || "",
        });
        const [phoneInputKey, setPhoneInputKey] = useState<number>(0);

        function validateForm(form: Omit<ApproverDto, "id">) {
            const result = modifyReleaseStrategySchema.safeParse(form);
            setIsValidForm(result.success);
        }

        const handleFormChange = (field: keyof ModifyReleaseStrategyDTO, value: string) => {
            setForm((prevForm) => {
                const newForm = {
                    ...prevForm,
                    [field]: value,
                };
                validateForm(newForm);
                return newForm;
            });
        };

        function handleOnSetForm(newForm: ApproverDto) {
            setForm((prevForm) => {
                if (prevForm.email === newForm.email && prevForm.name === newForm.name && prevForm.phone === newForm.phone) {
                    return prevForm;
                }
                validateForm(newForm);
                return newForm;
            });
            setPhoneInputKey((prev) => prev + 1);
        }

        function handleSubmit() {
            const result = modifyReleaseStrategySchema.safeParse(form);
            if (result.error) return;
            const payload: CreateApproverDto = {
                ...form,
                ...(releaseStrategy.roleType === RoleType.PROGRAM_MANAGER
                    ? { programAddress: releaseStrategy.address }
                    : { userId: releaseStrategy.admin.id }),
            };
            // if (releaseStrategy.approver?.id) {
            //     updateApproverMutation({ id: releaseStrategy.approver.id, body: payload });
            // } else {
            createApproverMutation(payload);
            // }
        }

        async function handleOnSuccessSend() {
            await queryClient.invalidateQueries(Queries.RELEASE_STRATEGY_FIND_ALL);
            showToast(translate("infoSavedSuccesfully"), { type: "success" });
            closeModal();
        }

        function closeModal() {
            hideModal(ModifyReleaseStrategyModal.id);
        }

        const modalTitle = () => (releaseStrategy.approver?.id ? translate("modifyReleaseStrategy") : translate("createReleaseStrategy"));

        return (
            <Modal
                title={modalTitle()}
                className={clsx("modify-movements-modal", className)}
                style={{ width: "700px", maxHeight: "none" }}
                {...rest}
            >
                <Col gap={20}>
                    <ModifyReleaseStrategyForm
                        approver={form}
                        onChange={handleFormChange}
                        onSetForm={handleOnSetForm}
                        phoneInputKey={phoneInputKey}
                    />
                    <Divider />
                    <Row justifyContent="center" gap={15}>
                        <ToolbarButton loading={isCreatingApprover || isUpdatingApprover} variant="outlined" onClick={closeModal}>
                            {translate("cancel")}
                        </ToolbarButton>
                        <ToolbarButton loading={isCreatingApprover || isUpdatingApprover} onClick={handleSubmit} disabled={!isValidForm}>
                            {translate("save")}
                        </ToolbarButton>
                    </Row>
                </Col>
            </Modal>
        );
    },
);

export default ModifyReleaseStrategyModal;
